import React from 'react'
import PropTypes from 'prop-types'
import { Transition, TransitionGroup } from 'react-transition-group'
import { gsap, ScrollSmoother, ScrollTrigger } from 'gsap-wrapper'
import ThemeProvider from 'styles/theme-provider'
import LocaleProvider from 'styles/locale-provider'
import useWindowSize from 'styles/window-size'
import Header from '../header'
import Footer from '../footer'
import BottomNavSection from '../bottom-nav-section'
import BottomBlackSection from '../bottom-black-section'
import HeaderTransition from '../ui-elements/header-transition'

import 'styles/fonts.css'
import GlobalStyle from 'styles/global'
import { LayoutWrapper, PageWrapper, ContentWrapper } from './index.styled'
import CookiesBanner from '../ui-elements/cookies-banner'

const LayoutContent = ({ children, location }) => {
  return (
    <TransitionGroup component={null}>
      <Transition key={location.pathname} timeout={{ enter: 1000, exit: 1000 }}>
        {(status) => (
          <div className={`page ${status}`}>
            <HeaderTransition status={status}>
              <LayoutWrapper>{children}</LayoutWrapper>
            </HeaderTransition>
          </div>
        )}
      </Transition>
    </TransitionGroup>
  )
}

const Layout = (props) => {
  const {
    children,
    location,
    pageContext: { layout },
  } = props
  const refWrapper = React.useRef(null)
  const scrollSmoother = React.useRef(null)
  const { isMobile, width } = useWindowSize()
  const tr1 = []
  const tr2 = []
  const isHome =
    location.pathname === '/' ||
    location.pathname === '/en/' ||
    location.pathname === '/es/'

  React.useLayoutEffect(() => {
    const gsapContext = gsap.context(() => {
      scrollSmoother.current = ScrollSmoother.create({
        content: '.home-content',
        wrapper: '.home-wrapper',
        smooth: /* isMobile ? 0 :  */ 2,
        effects: true,
        normalizeScroll: { allowNestedScroll: true },
      })

      // fadeIn
      gsap.utils
        .toArray('.fadeIn, .videoTextFadeIn')
        .forEach(function (container) {
          tr1.push(
            ScrollTrigger.create({
              trigger: container,
              start: 'top 93%',
              end: '+=1000%',
              toggleClass: {
                targets: container,
                className: 'visible',
                toggle: 'add',
              },
              once: true,
            }),
          )
        })

      // imgPallarax
      gsap.utils.toArray('.imgPallarax').forEach(function (container) {
        tr2.push(
          ScrollTrigger.create({
            trigger: container,
            start: 'top 93%',
            end: '+=1000%',
            toggleClass: {
              targets: container,
              className: 'visible',
              toggle: 'add',
            },
            once: true,
          }),
        )
      })
    }, [refWrapper])

    return () => gsapContext.revert()
  }, [children, isMobile])

  // Scroll to anchor
  React.useEffect(() => {
    if (location.hash) {
      const timeout = setTimeout(() => {
        const hashWithoutHash = location.hash.substring(1)
        const targetElement = document.querySelector(
          `#${hashWithoutHash}-section`,
        )

        if (targetElement) {
          gsap.to(scrollSmoother.current, {
            scrollTop: targetElement.offsetTop - 90,
            duration: 2,
          })
        }
      }, 100)
      return () => clearTimeout(timeout)
    }
  }, [location.hash])

  return (
    <LocaleProvider location={location}>
      <ThemeProvider location={location} scrollSmoother={scrollSmoother}>
        <Header location={location} isHome={isHome} />
        <div className="home-wrapper" ref={refWrapper}>
          <div className="home-content">
            <PageWrapper>
              <GlobalStyle />
              <ContentWrapper>
                <LayoutContent location={location}>
                  {children}
                  <BottomBlackSection className={layout} />
                  <BottomNavSection className={layout} />
                </LayoutContent>
              </ContentWrapper>

              <Footer className={layout} />
            </PageWrapper>
          </div>
        </div>
        <CookiesBanner />
      </ThemeProvider>
    </LocaleProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
}

export default Layout
